'use client';

import { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Alert,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';
import axiosClient from '../../api/axiosClient';
import { v4 as uuidv4 } from 'uuid';

export interface ContractData {
  title: string;
  onboardingDate: string | null;
  startDate: string | null;
  endDate: string | null;
  position: string;
  categoryId: number | null;
  registrationNumber: string;
  remoteWorkSurfaceArea: number | null;
  id?: number;
  status?: 'ACTIVE' | 'EXPIRED';
}


type ContractDrawerProps = {
  open: boolean;
  onClose: () => void;
  employeeId?: number | null;
  editingContract?: { id: number } | ContractData | null;
  onSuccess?: () => void;
};

export default function ContractDrawer({
  open,
  onClose,
  employeeId,
  editingContract,
  onSuccess,
}: ContractDrawerProps) {
  const [formData, setFormData] = useState<ContractData>({
    title: '',
    onboardingDate: null,
    startDate: null,
    endDate: null,
    position: '',
    categoryId: 1,
    registrationNumber: '',
    remoteWorkSurfaceArea: 0,
  });

  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);


  const formatDateForInput = (dateStr: string | null) =>
    dateStr ? dateStr.split('T')[0] : '';

  const normalizeDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    return dateStr.split('T')[0];
  };


  useEffect(() => {
    const fetchContract = async () => {
      if (!editingContract || !employeeId) return;

      if ('id' in editingContract && Object.keys(editingContract).length === 1) {
        try {
          setLoading(true);
          const res = await axiosClient.get(
            `/employees/${employeeId}/contracts/${editingContract.id}`
          );

          const data = res.data.contract || res.data;

          setFormData({
            title: data.title || '',
            onboardingDate: data.onboardingDate || null,
            startDate: data.startDate || null,
            endDate: data.endDate || null,
            position: data.position || '',
            categoryId: data.categoryId || 1,
            registrationNumber: data.registrationNumber || '',
            remoteWorkSurfaceArea: data.remoteWorkSurfaceArea || 0,
            id: data.id,
            status: data.status,
          });
        } catch (err) {
          setError('Erreur lors du chargement du contrat');
        } finally {
          setLoading(false);
        }
      } else if (Object.keys(editingContract).length > 0) {
        setFormData(editingContract as ContractData);
      }
    };

    fetchContract();
  }, [editingContract, employeeId]);


  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const apiKey = localStorage.getItem('apiKey');
        if (!apiKey) return;

        const res = await axiosClient.get('/categories', {
          headers: { Authorization: `Bearer ${apiKey}` },
        });

        setCategories(res.data.categories || []);

        if (!editingContract && res.data.categories.length > 0) {
          setFormData((prev) => ({ ...prev, categoryId: res.data.categories[0].id }));
        }
      } catch (err) {
        console.error('Erreur fetch categories', err);
      }
    };

    fetchCategories();
  }, [editingContract]);

 const handleChange = (field: keyof ContractData, value: any) => {
  setFormData((prev) => {
    let parsedValue = value;


    if (value === '') {
      parsedValue = null;
    }
    if (['categoryId', 'remoteWorkSurfaceArea'].includes(field)) {
      parsedValue = value === '' ? null : Number(value);
    }

    return {
      ...prev,
      [field]: parsedValue,
    };
  });
};


  const handleSubmit = async () => {
    if (!employeeId) return;
    setError(null);
    setSuccessMsg(null);
    setLoading(true);

    const payload = {
      ...formData,
      onboardingDate: normalizeDate(formData.onboardingDate),
      startDate: normalizeDate(formData.startDate),
      endDate: normalizeDate(formData.endDate),
    };

    try {
      const apiKey = localStorage.getItem('apiKey');
      if (!apiKey) throw new Error('API key missing');

      const headers = {
        Authorization: `Bearer ${apiKey}`,
        'Idempotency-Key': uuidv4(),
      };

      if (editingContract && 'id' in editingContract && editingContract.id) {
        await axiosClient.put(
          `/employees/${employeeId}/contracts/${editingContract.id}`,
          payload,
          { headers }
        );
        setSuccessMsg('Contract updated successfully!');
      } else {
        await axiosClient.post(`/employees/${employeeId}/start-contract`, payload, {
          headers,
        });
        setSuccessMsg('Contract created successfully!');
      }

      onSuccess?.();
      setTimeout(() => onClose(), 1000);
    } catch (err: any) {
      console.error(err);
      const errMsg =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        'Erreur lors de la sauvegarde du contrat';
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleEndContract = async () => {
    if (!employeeId || !editingContract || !('id' in editingContract)) return;

    setError(null);
    setSuccessMsg(null);
    setLoading(true);

    try {
      const apiKey = localStorage.getItem('apiKey');
      if (!apiKey) throw new Error('API key missing');

      const headers = { Authorization: `Bearer ${apiKey}` };

      const endDate = normalizeDate(formData.endDate) || new Date().toISOString().split('T')[0];

      const res = await axiosClient.post(
        `/employees/${employeeId}/contracts/${editingContract.id}/end-contract`,
        { endDate },
        { headers }
      );

      setFormData(res.data.contract);
      setSuccessMsg('Contract ended successfully!');
      onSuccess?.();
      setTimeout(() => onClose(), 1000);
    } catch (err: any) {
      console.error(err);

      const errMsg =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        'Erreur lors de la fin du contrat';
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 450, p: 4, borderRadius: '16px 0 0 16px' } }}
    >
      <motion.div
        initial={{ x: 300, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 300, opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
    
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">
            {editingContract ? 'Edit Contract' : 'New Contract'}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>


        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}


        {loading ? (
          <Stack alignItems="center" sx={{ mt: 3 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <>
        
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Title"
                value={formData.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                fullWidth
              />

              <Stack direction="row" spacing={2}>
                <TextField
                  label="Onboarding Date"
                  type="date"
                  value={formatDateForInput(formData.onboardingDate)}
                  onChange={(e) => handleChange('onboardingDate', e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="Start Date"
                  type="date"
                  value={formatDateForInput(formData.startDate)}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>

              <TextField
                label="End Date"
                type="date"
                value={formatDateForInput(formData.endDate)}
                onChange={(e) => handleChange('endDate', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Position"
                value={formData.position || ''}
                onChange={(e) => handleChange('position', e.target.value)}
                fullWidth
              />

              <TextField
                select
                label="Category"
                value={formData.categoryId}
                onChange={(e) => handleChange('categoryId', Number(e.target.value))}
                fullWidth
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Registration Number"
                value={formData.registrationNumber || ''}
                onChange={(e) => handleChange('registrationNumber', e.target.value)}
                fullWidth
              />

              <TextField
                label="Remote Work Surface Area"
                type="number"
                value={formData.remoteWorkSurfaceArea}
                onChange={(e) => handleChange('remoteWorkSurfaceArea', e.target.value)}
                fullWidth
              />
              <Button
                variant="contained"
                disabled={loading}
                onClick={handleSubmit}
                sx={{
                  mt: 2,
                  backgroundColor: '#714291',
                  '&:hover': { backgroundColor: '#5a336f' },
                  textTransform: 'none',
                }}
              >
                {editingContract ? 'Save' : 'Create'}
              </Button>

              {/* End contract */}
              {editingContract && formData.status === 'ACTIVE' && (
                <Button
                  variant="outlined"
                  color="error"
                  disabled={loading}
                  onClick={handleEndContract}
                  sx={{ mt: 1, textTransform: 'none' }}
                >
                  End Contract
                </Button>
              )}
            </Box>
          </>
        )}
      </motion.div>
    </Drawer>
  );
}
